/**
 * TrendLane — product spotlight carousel (v3: fixed tiles, sliding content)
 *
 * Used by sections/ss-collection-promo.liquid and sections/ss-tabbed-grid.liquid.
 *
 * The outer layout is a permanently fixed 5-cell CSS Grid (see
 * assets/ss-styles.css): [big image] [big info text] [small] [small] [small].
 * These five cells never move, resize, or change position while the
 * carousel is used. Instead, each cell is its own tiny viewport/track/page
 * mini-carousel, and a single circular "active" index per spotlight drives
 * all five cells at once: cell 0 (image) and cell 1 (info) both show the
 * product at `active`; cells 2-4 (small cards) show `active+1`, `active+2`,
 * `active+3` (all mod product count). Pressing next/prev advances/retreats
 * `active` and slides fresh content into every cell simultaneously, so a
 * product visually migrates cell-by-cell from the small row into the big
 * slot (or back out), while the tiles themselves never budge. The index
 * wraps around indefinitely — there is no first/last product, so the
 * arrows are never disabled. There is no autoplay; only the arrows move it.
 *
 * Markup contract, inside one [data-ss-spotlight-root]:
 *   [data-ss-spotlight]         wraps the whole carousel
 *     [data-ss-spotlight-prev]  prev-arrow button
 *     [data-ss-spotlight-next]  next-arrow button
 *   [data-ss-spotlight-rail]    one or more rails (grids); only one should
 *                                be visible at a time — for a tabbed layout,
 *                                call SSSpotlight.bind(root) again after
 *                                switching panels
 *     [data-ss-spotlight-source] hidden block holding every available
 *                                product as one .ss-source-item per product:
 *                                  .ss-source-media  (rendered <img>/badge)
 *                                  .ss-source-title  (plain title text)
 *                                  .ss-source-price  (rendered price markup)
 *                                optional [data-url] attribute for the link
 *     [data-ss-slot]             exactly 5, in document order, each with
 *                                data-role="media" | "info" | "small"
 *       .ss-slot-viewport > .ss-slot-track > .ss-slot-page   (the sliding bit)
 */
window.SSSpotlight = (function () {
  var TRANSITION_MS = 450;

  function isVisible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  function readProducts(rail) {
    var source = rail.querySelector('[data-ss-spotlight-source]');
    if (!source) return [];
    var items = Array.prototype.slice.call(source.querySelectorAll('.ss-source-item'));
    return items.map(function (item) {
      var media = item.querySelector('.ss-source-media');
      var title = item.querySelector('.ss-source-title');
      var price = item.querySelector('.ss-source-price');
      return {
        url: item.getAttribute('data-url') || '',
        mediaHtml: media ? media.innerHTML : '',
        titleHtml: title ? title.innerHTML : '',
        priceHtml: price ? price.innerHTML : ''
      };
    });
  }

  function buildMediaHtml(p) {
    var tag = p.url ? 'a' : 'div';
    var attrs = p.url ? ' href="' + p.url + '"' : '';
    return '<' + tag + ' class="ss-slot-media"' + attrs + '>' +
      '<div class="ss-slot-media__frame">' + p.mediaHtml + '</div>' +
      '</' + tag + '>';
  }

  function buildInfoHtml(p) {
    var tag = p.url ? 'a' : 'div';
    var attrs = p.url ? ' href="' + p.url + '"' : '';
    return '<' + tag + ' class="ss-slot-info"' + attrs + '>' +
      '<p class="ss-slot-info__title">' + p.titleHtml + '</p>' +
      '<p class="ss-slot-info__price">' + p.priceHtml + '</p>' +
      '</' + tag + '>';
  }

  function buildSmallHtml(p) {
    var tag = p.url ? 'a' : 'div';
    var attrs = p.url ? ' href="' + p.url + '"' : '';
    return '<' + tag + ' class="ss-card"' + attrs + '>' +
      '<div class="ss-card__media">' + p.mediaHtml + '</div>' +
      '<div class="ss-card__info">' +
      '<p class="ss-card__title">' + p.titleHtml + '</p>' +
      '<p class="ss-card__price">' + p.priceHtml + '</p>' +
      '</div>' +
      '</' + tag + '>';
  }

  function htmlForSlot(role, p) {
    if (role === 'media') return buildMediaHtml(p);
    if (role === 'info') return buildInfoHtml(p);
    return buildSmallHtml(p);
  }

  // Slides the content of one fixed-size slot from whatever it currently
  // shows to `html`. dir === 1 slides the new content in from the right
  // (old content exits left) — used on "next". dir === -1 slides the new
  // content in from the left (old content exits right) — used on "prev".
  // The slot element itself never resizes or moves; only its internal
  // track's transform animates.
  function slideSlot(slot, html, dir) {
    var track = slot.querySelector('.ss-slot-track');
    if (!track) return;
    var oldPage = track.querySelector('.ss-slot-page');
    var newPage = document.createElement('div');
    newPage.className = 'ss-slot-page';
    newPage.innerHTML = html;

    if (!oldPage) {
      // First paint — nothing to animate from.
      track.appendChild(newPage);
      return;
    }

    if (dir === 1) {
      track.appendChild(newPage);
      track.style.transition = 'none';
      track.style.transform = 'translateX(0%)';
      void track.offsetWidth; // force reflow so the "none" transition sticks
      track.style.transition = 'transform ' + TRANSITION_MS + 'ms cubic-bezier(0.4,0,0.2,1)';
      track.style.transform = 'translateX(-100%)';
    } else {
      track.insertBefore(newPage, oldPage);
      track.style.transition = 'none';
      track.style.transform = 'translateX(-100%)';
      void track.offsetWidth;
      track.style.transition = 'transform ' + TRANSITION_MS + 'ms cubic-bezier(0.4,0,0.2,1)';
      track.style.transform = 'translateX(0%)';
    }

    var cleaned = false;
    function cleanup() {
      if (cleaned) return;
      cleaned = true;
      track.removeEventListener('transitionend', cleanup);
      track.style.transition = 'none';
      if (oldPage.parentNode === track) track.removeChild(oldPage);
      track.style.transform = 'translateX(0%)';
      void track.offsetWidth;
      track.style.transition = '';
    }
    track.addEventListener('transitionend', cleanup);
    // Safety net in case transitionend never fires (e.g. tab backgrounded).
    setTimeout(cleanup, TRANSITION_MS + 120);
  }

  function bind(root) {
    if (!root) return;
    var carousel = root.querySelector('[data-ss-spotlight]');
    if (!carousel) return;

    var prevBtn = carousel.querySelector('[data-ss-spotlight-prev]');
    var nextBtn = carousel.querySelector('[data-ss-spotlight-next]');

    var rails = root.querySelectorAll('[data-ss-spotlight-rail]');
    var rail = null;
    for (var r = 0; r < rails.length; r++) {
      if (isVisible(rails[r])) {
        rail = rails[r];
        break;
      }
    }
    if (!rail && rails.length) rail = rails[0];
    if (!rail) return;

    var products = readProducts(rail);
    var n = products.length;
    if (!n) return;

    var slots = Array.prototype.slice.call(rail.querySelectorAll('[data-ss-slot]'));
    if (!slots.length) return;

    var active = 0;
    var busy = false;

    function offsetForSlot(index) {
      var role = slots[index].getAttribute('data-role');
      if (role === 'media' || role === 'info') return 0;
      // The three small slots appear in document order after media/info —
      // figure out which "small" position this is (0, 1, 2).
      var smallIndex = 0;
      for (var i = 0; i < index; i++) {
        var r = slots[i].getAttribute('data-role');
        if (r !== 'media' && r !== 'info') smallIndex++;
      }
      return smallIndex + 1;
    }

    function paint(dir) {
      slots.forEach(function (slot, i) {
        var role = slot.getAttribute('data-role');
        var offset = offsetForSlot(i);
        var product = products[(active + offset) % n];
        var html = htmlForSlot(role, product);
        if (dir === 0) {
          var track = slot.querySelector('.ss-slot-track');
          if (track) track.innerHTML = '';
          slideSlot(slot, html, 0);
        } else {
          slideSlot(slot, html, dir);
        }
      });
    }

    function goNext() {
      if (busy || n < 2) return;
      busy = true;
      active = (active + 1) % n;
      paint(1);
      setTimeout(function () { busy = false; }, TRANSITION_MS + 60);
    }

    function goPrev() {
      if (busy || n < 2) return;
      busy = true;
      active = (active - 1 + n) % n;
      paint(-1);
      setTimeout(function () { busy = false; }, TRANSITION_MS + 60);
    }

    // Assigning .onclick (rather than addEventListener) means re-binding
    // this same button after a tab switch simply replaces the handler
    // instead of stacking a second one.
    if (prevBtn) prevBtn.onclick = goPrev;
    if (nextBtn) nextBtn.onclick = goNext;

    paint(0);
  }

  function bindAll(context) {
    (context || document).querySelectorAll('[data-ss-spotlight-root]').forEach(bind);
  }

  document.addEventListener('DOMContentLoaded', function () {
    bindAll(document);
  });
  document.addEventListener('shopify:section:load', function (e) {
    bindAll(e.target);
  });

  return { bind: bind, bindAll: bindAll };
})();
